/**
 * The setup checklist (`onboarding`): what a new restaurant has done, and
 * what is next. Each step is worked out from the restaurant's data on the
 * server, so a step done any way — typed, imported, set up by support — ticks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export interface SetupStep {
  key: 'tax' | 'menu' | 'tables' | 'till' | 'team' | 'first_sale' | string;
  title: string;
  why: string;
  /** A back-office path, or `till` for something done on the till. */
  where: string;
  action: string;
  skippable: boolean;
  done: boolean;
  skipped: boolean;
}

export interface SetupChecklist {
  steps: SetupStep[];
  complete: boolean;
  hidden: boolean;
  /** The first-sign-in guided setup was finished or put off. */
  wizard_done: boolean;
  /** Guides somebody put away: `prep`, `stock`, `recipes`, `buying`, `kitchen`. */
  guides_put_away: string[];
}

/** The step-by-step guides on the harder screens. */
export type GuideName = 'prep' | 'stock' | 'recipes' | 'buying' | 'kitchen';

export function useSetupChecklist(options?: { enabled?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: ['setup'],
    queryFn: ({ signal }) => api.get<SetupChecklist>('/v1/setup', signal),
    enabled: options?.enabled ?? true,
    // Fresh whenever the Overview is opened again: a step is usually done
    // on another page, and the list should already know.
    staleTime: 0,
  });
}

/**
 * Skip a step that does not apply, or put something away — `checklist`, the
 * `wizard`, or a guide as `guide:prep`. `skipped: false` brings it back.
 */
export function useSkipSetupStep() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<SetupChecklist, ApiError, { step: string; skipped: boolean }>({
    mutationFn: (body) =>
      api.request<SetupChecklist>('/v1/setup/skips', { method: 'PUT', body }),
    onSuccess: (checklist) => queryClient.setQueryData(['setup'], checklist),
  });
}
